import { useEffect } from 'react';
import { format } from 'date-fns';
import {
  Bell,
  Package,
  WarningCircle,
  CheckCircle,
  Info,
  Trash,
  Check,
  CreditCard,
} from '@phosphor-icons/react';
import { Link } from 'react-router-dom';
import { useSystemNotificationStore, SystemNotificationType, useAuthStore } from '@dukene/shared';

const iconMap: Record<SystemNotificationType, React.ElementType> = {
  ORDER: Package,
  INVENTORY: WarningCircle,
  SYSTEM: Info,
  PAYMENT: CreditCard,
};

const colorMap: Record<SystemNotificationType, string> = {
  ORDER: 'text-[#2C6ECB] bg-[#EBF5FF] dark:bg-blue-950/40 dark:text-blue-400',
  INVENTORY: 'text-[#B98900] bg-[#FFF5EA] dark:bg-amber-950/40 dark:text-amber-400',
  SYSTEM: 'text-[#5C5F62] bg-[#F6F6F7] dark:bg-slate-800 dark:text-slate-300',
  PAYMENT: 'text-[#006838] bg-[#E3F1DF] dark:bg-emerald-950/40 dark:text-emerald-400',
};

export default function Notifications() {
  const { activeStoreId } = useAuthStore();
  const {
    notifications,
    fetchNotifications,
    markAsRead,
    markAllAsRead,
    removeNotification,
    unreadCount,
  } = useSystemNotificationStore();

  useEffect(() => {
    if (activeStoreId) {
      fetchNotifications();
    }
  }, [fetchNotifications, activeStoreId]);

  return (
    <div className="max-w-5xl mx-auto space-y-6 pb-12">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-bold text-[#202223] dark:text-white">Notifications</h1>
            {unreadCount > 0 && (
              <span className="px-2.5 py-0.5 bg-emerald/10 text-emerald text-xs font-bold rounded-full">
                {unreadCount} unread
              </span>
            )}
          </div>
          <p className="text-sm text-[#5C5F62] dark:text-slate-400 mt-1">
            Stay updated with recent orders, inventory alerts, and system activity for your store.
          </p>
        </div>
        {unreadCount > 0 && (
          <div className="flex gap-3">
            <button
              onClick={() => markAllAsRead()}
              className="px-4 py-2 text-sm font-medium text-[#202223] dark:text-white bg-white dark:bg-[#1A1D21] border border-[#D2D5D9] dark:border-slate-800 hover:bg-[#F6F6F7] dark:hover:bg-slate-800 rounded-lg transition-colors flex items-center gap-2 shadow-sm"
            >
              <Check weight="bold" className="w-4 h-4 text-emerald" />
              Mark all as read
            </button>
          </div>
        )}
      </div>

      {/* Notifications Card */}
      <div className="bg-white dark:bg-[#1A1D21] rounded-xl border border-[#D2D5D9] dark:border-slate-800 shadow-sm overflow-hidden divide-y divide-[#E1E3E5] dark:divide-slate-800">
        {notifications.length === 0 ? (
          <div className="p-16 text-center">
            <div className="w-16 h-16 bg-[#F6F6F7] dark:bg-slate-800/80 rounded-2xl flex items-center justify-center mx-auto mb-4 text-[#5C5F62] dark:text-slate-400">
              <Bell weight="duotone" className="w-8 h-8" />
            </div>
            <h3 className="text-base font-semibold text-[#202223] dark:text-white mb-1">
              No notifications yet
            </h3>
            <p className="text-sm text-[#5C5F62] dark:text-slate-400 max-w-sm mx-auto">
              You're all caught up! New updates regarding store activity and alerts will appear here.
            </p>
          </div>
        ) : (
          notifications
            .filter((notification) => notification && notification.id && notification.type)
            .map((notification) => {
              const Icon = iconMap[notification.type] || Bell;
              const colorClass = colorMap[notification.type] || 'text-[#5C5F62] bg-[#F6F6F7] dark:bg-slate-800 dark:text-slate-300';

              return (
                <div
                  key={notification.id}
                  className={`p-5 hover:bg-[#F6F6F7]/60 dark:hover:bg-slate-800/40 transition-colors flex gap-4 relative group ${
                    !notification.read ? 'bg-emerald/[0.03] dark:bg-emerald/5' : ''
                  }`}
                >
                  {!notification.read && (
                    <span className="absolute left-2 top-6 w-2 h-2 rounded-full bg-emerald ring-4 ring-emerald/10" />
                  )}

                  {/* Icon */}
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${colorClass}`}>
                    <Icon weight="fill" className="w-5 h-5" />
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-2">
                      <div>
                        <h3
                          className={`text-sm font-semibold mb-1 ${
                            !notification.read ? 'text-[#202223] dark:text-white' : 'text-[#303030] dark:text-slate-300'
                          }`}
                        >
                          {notification.title}
                        </h3>
                        <p className="text-sm text-[#5C5F62] dark:text-slate-400 leading-relaxed mb-2.5">
                          {notification.message}
                        </p>
                        <div className="flex items-center gap-4 text-xs text-[#8C9196] dark:text-slate-500 font-medium">
                          <span>
                            {(() => {
                              try {
                                const d = new Date(notification.createdAt);
                                return !isNaN(d.getTime()) ? format(d, 'MMM d, yyyy h:mm a') : 'Recent';
                              } catch {
                                return 'Recent';
                              }
                            })()}
                          </span>
                          {notification.link && (
                            <Link
                              to={notification.link}
                              className="text-emerald hover:underline font-semibold"
                              onClick={() => {
                                if (!notification.read) markAsRead(notification.id);
                              }}
                            >
                              View Details
                            </Link>
                          )}
                        </div>
                      </div>

                      {/* Actions */}
                      <div className="flex items-center gap-1 sm:opacity-0 group-hover:opacity-100 transition-opacity self-end sm:self-start mt-2 sm:mt-0">
                        {!notification.read && (
                          <button
                            onClick={() => markAsRead(notification.id)}
                            className="p-1.5 text-[#5C5F62] dark:text-slate-400 hover:text-emerald hover:bg-emerald/10 rounded-lg transition-colors"
                            title="Mark as read"
                          >
                            <CheckCircle weight="bold" className="w-4 h-4" />
                          </button>
                        )}
                        <button
                          onClick={() => removeNotification(notification.id)}
                          className="p-1.5 text-[#5C5F62] dark:text-slate-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-950/50 rounded-lg transition-colors"
                          title="Delete"
                        >
                          <Trash weight="bold" className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })
        )}
      </div>
    </div>
  );
}
